import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Wordcloud } from "@visx/wordcloud";

export default function WordCloudComponent({
  words = [],
  loading = false,
  onWordClick,
}) {
  const cloudSizerRef = useRef(null);
  const [cloudSize, setCloudSize] = useState({ width: 900, height: 480 });

  const normalizedWords = useMemo(
    () =>
      (words || [])
        .map((item) => ({
          text: item?.name,
          value: item?.value,
        }))
        .filter((item) => item.text && item.value > 0),
    [words],
  );

  useEffect(() => {
    if (!cloudSizerRef.current) return;

    const updateCloudSize = () => {
      const width = Math.floor(cloudSizerRef.current.clientWidth || 0);
      if (width <= 0) return;
      const height = Math.max(200, Math.round((width * 480) / 900));
      setCloudSize({ width, height });
    };

    updateCloudSize();
    const observer = new ResizeObserver(updateCloudSize);
    observer.observe(cloudSizerRef.current);

    return () => observer.disconnect();
  }, []);

  const fontSize = useMemo(() => {
    const maxValue = normalizedWords.reduce(
      (max, item) => Math.max(max, item.value || 0),
      0,
    );
    return (word) => {
      if (!maxValue) return 12;
      const ratio = Math.sqrt(word.value / maxValue);
      return Math.max(12, Math.round(12 + ratio * (40 - 12)));
    };
  }, [normalizedWords]);

  if (loading) {
    return (
      <Box sx={styles.container}>
        <CircularProgress size={25} sx={{ color: "#003300" }} />
      </Box>
    );
  }

  if (!normalizedWords.length) {
    return (
      <Box sx={styles.container}>
        <Box component="p" sx={styles.emptyText}>
          No word cloud data available
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Box ref={cloudSizerRef} sx={styles.cloudSizer}>
        <Wordcloud
          words={normalizedWords.slice(0, 120)}
          width={cloudSize.width}
          height={cloudSize.height}
          rotate={() => 0}
          padding={8}
          spiral="archimedean"
          fontSize={fontSize}
          random={() => 0.5}
          font="LarsseitMedium"
        >
          {(cloudWords) =>
            cloudWords.map((word, i) => (
              <text
                key={`${word.text}-${i}`}
                textAnchor="middle"
                transform={`translate(${word.x}, ${word.y}) rotate(${word.rotate})`}
                fontSize={word.size}
                fontFamily={word.font}
                fill={WORD_COLORS[i % WORD_COLORS.length]}
                style={styles.wordText}
                onClick={() => onWordClick?.(word.text)}
              >
                {word.text}
              </text>
            ))
          }
        </Wordcloud>
      </Box>
    </Box>
  );
}

const WORD_COLORS = [
  "#FF6D00",
  "#FF8F00",
  "#FF3D00",
  "#F44336",
  "#E53935",
  "#43A047",
  "#66BB6A",
  "#2E7D32",
  "#7732a8",
  "#5204c7",
];

const styles = {
  container: {
    padding: "33px",
    background: "#FFFFFF",
    borderRadius: "8px",
    border: "1px solid #dee2e0",
    boxShadow: "0 2px 4px 0 rgba(196, 201, 199, 0.5)",
    mt: "14px",
    minHeight: "520px",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cloudSizer: {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    aspectRatio: "900 / 480",
    position: "relative",
  },
  wordText: { cursor: "pointer", userSelect: "none" },
  emptyText: {
    margin: 0,
    fontFamily: "FustatMedium",
    fontSize: "14px",
    color: "#0C2728",
  },
};
